import { AnimatePresence, motion } from 'motion/react';
import { useRouter } from 'next/router';
import { Popover as RadixPopover } from 'radix-ui';
import { Children, cloneElement, isValidElement, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from '@/lib/twMerge';
import { useAccountModals } from '@/components/Modals/Account';
import Tooltip from '@/components/Tooltip';
import { Drawer, DrawerContent, DrawerTrigger } from '@music-vine/cadence/ui';
import useTWScreens from '@/hooks/ui/useTWScreens';
import { PopoverProvider } from './context';
import type { TooltipProps } from '@/components/Tooltip';
import type { ReactNode } from 'react';
interface PopoverContainerProps {
  align?: 'start' | 'end';
  animateOverTrigger?: boolean;
  avoidCollisions?: boolean;
  buttonFade?: boolean;
  children: ReactNode;
  className?: string;
  customHandleColor?: string;
  handleColor?: 'default' | 'dark' | 'light' | 'custom';
  onOpenChange?: (isOpen: boolean) => void;
  side?: 'top' | 'bottom';
}
interface SlotWithChildren {
  children: React.ReactElement;
}
interface TriggerProps {
  children?: React.ReactElement;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  ref?: React.Ref<HTMLDivElement>;
  tooltip?: Omit<TooltipProps, 'children'>;
}
const PopoverTrigger = ({
  children,
  tooltip,
  ref,
  ...props
}: TriggerProps) => {
  const {
    isXs
  } = useTWScreens();
  if (children) {
    const clonedChildren = cloneElement(children, {
      ref,
      ...props
    } as Partial<unknown>);
    if (!(isXs || tooltip?.forceShow !== undefined)) {
      return clonedChildren;
    }
    const tooltipMode = tooltip?.forceShow === undefined ? 'hover' : 'force';
    return <Tooltip key={tooltipMode} {...tooltip}>
        {clonedChildren}
      </Tooltip>;
  }
  return null;
};
PopoverTrigger.displayName = 'PopoverTrigger';
const PopoverContent: React.FC<SlotWithChildren> = ({
  children
}) => children;
const cardAnimations = {
  initial: {
    opacity: 0,
    scale: 0.75
  },
  animate: {
    opacity: 1,
    scale: 1
  },
  exit: {
    opacity: 0,
    scale: 0.75
  }
};
const triggerAnimation = {
  initial: {
    opacity: 1,
    scale: 1,
    transformOrigin: 'top right'
  },
  animate: {
    opacity: 0,
    scale: 1.2,
    transformOrigin: 'top right'
  },
  exit: {
    opacity: 1,
    scale: 1,
    transformOrigin: 'top right'
  }
};
const fadeAnimation = {
  initial: {
    opacity: 1
  },
  animate: {
    opacity: 0
  },
  exit: {
    opacity: 1
  }
};
const PopoverContainer: React.FC<PopoverContainerProps> = memo(({
  children,
  className,
  onOpenChange,
  align = 'end',
  side = 'bottom',
  animateOverTrigger = false,
  buttonFade = false,
  avoidCollisions = false,
  handleColor,
  customHandleColor
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const {
    currentModal
  } = useAccountModals();
  const router = useRouter();
  const {
    isXs
  } = useTWScreens();
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useMemo(() => !isXs, [isXs]);

  // Track if the popover is closing due to navigation to avoid focusing trigger
  const isClosingDueToNavigationRef = useRef(false);
  const isClosingDueToPointerRef = useRef(false);

  // Focus the trigger when the popover is closed (but not during navigation).
  const handleOpenChange = useCallback((shouldOpen: boolean) => {
    setIsOpen(prevIsOpen => {
      if (prevIsOpen === shouldOpen) {
        return prevIsOpen;
      }
      if (!shouldOpen && prevIsOpen) {
        if (!(isClosingDueToNavigationRef.current || isClosingDueToPointerRef.current)) {
          buttonRef.current?.focus();
        }
        isClosingDueToNavigationRef.current = false;
        isClosingDueToPointerRef.current = false;
      } else if (shouldOpen) {
        // Focus the container when opening
        setTimeout(() => {
          containerRef?.current?.focus();
        }, 0);
      }

      // Call onOpenChange directly instead of using useEffect
      if (onOpenChange) {
        onOpenChange(shouldOpen);
      }
      return shouldOpen;
    });
  }, [onOpenChange]);
  const handleDrawerOpenChange = useCallback((shouldOpen: boolean) => {
    setIsOpen(shouldOpen);
    onOpenChange?.(shouldOpen);
  }, [onOpenChange]);
  let trigger: ReactNode;
  let content: ReactNode;

  // Grab the trigger and content components.
  Children.forEach(children, child => {
    if (isValidElement(child)) {
      try {
        // Check if the child is a PopoverTrigger component
        if (child.type === PopoverTrigger) {
          // Clone the child element and add necessary props
          trigger = <PopoverTrigger ref={buttonRef} {...child.props as TriggerProps} />;
        } else if (child.type === PopoverContent) {
          content = child;
        }
      } catch {
        // Throw an error if the required components are not found
        throw new Error('Popover requires a trigger and content');
      }
    }
  });
  if (!(trigger && content)) {
    throw new Error('Popover requires a trigger and content');
  }

  // Set up ResizeObserver to handle screen size changes
  // Close popover when transitioning from mobile to desktop to prevent buggy anchor
  useEffect(() => {
    let prevWasMobile = window.innerWidth < 576; // xs breakpoint

    const handleResize = () => {
      const currentIsMobile = window.innerWidth < 576; // xs breakpoint

      // Only close when transitioning from mobile to desktop while popover is open
      if (prevWasMobile && !currentIsMobile && isOpen) {
        setIsOpen(false);
      }
      prevWasMobile = currentIsMobile;
    };
    const resizeObserver = new ResizeObserver(handleResize);

    // Observe the document body for resize changes
    if (document.body) {
      resizeObserver.observe(document.body);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, [isOpen]);
  useEffect(() => {
    // Mark that we're closing due to navigation to avoid focusing trigger
    isClosingDueToNavigationRef.current = true;
    // Only update state, don't call onOpenChange for route changes
    // to avoid notifying parent about programmatic closes
    setIsOpen(false);
  }, [router.pathname]);

  /*
   *  Close the hover card when the user opens an account modal
   *  eg. Sign up, log in, upgrade, etc.
   */
  useEffect(() => {
    if (currentModal !== null) {
      setIsOpen(false);
    }
  }, [currentModal]);

  // If the user is on mobile, use the Drawer component
  if (isMobile) {
    return <PopoverProvider value={{
      isOpen,
      setIsOpen
    }}>
          <Drawer onOpenChange={handleDrawerOpenChange} open={isOpen}>
            <DrawerTrigger>{trigger}</DrawerTrigger>
            <DrawerContent className={className} customHandleColor={customHandleColor} handleColor={handleColor}>
              {content}
            </DrawerContent>
          </Drawer>
        </PopoverProvider>;
  }
  const sideOffset = buttonRef.current?.offsetHeight ? -((buttonRef.current?.offsetHeight ?? 0) + 8) : 0; // Add 16 pixels to make sure buttons behind are obscured

  // If the user is on desktop, use a Popover
  return <PopoverProvider value={{
    isOpen,
    setIsOpen
  }}>
        <RadixPopover.Root onOpenChange={handleOpenChange} open={isOpen}>
          <div className="flex h-full items-center justify-center">
            <motion.div animate={isOpen && (animateOverTrigger || buttonFade) ? 'animate' : 'initial'} className="flex items-center justify-center will-change-transform *:cursor-pointer" exit="exit" initial="initial" transition={{
          type: 'spring',
          duration: 0.3,
          bounce: 0
        }} variants={buttonFade ? fadeAnimation : animateOverTrigger ? triggerAnimation : undefined}>
              <RadixPopover.Trigger asChild>{trigger}</RadixPopover.Trigger>
            </motion.div>
          </div>
          <RadixPopover.Portal forceMount>
            <AnimatePresence mode="sync">
              {isOpen && <RadixPopover.Content align={align} asChild avoidCollisions={avoidCollisions} className="z-50 rounded-[0.625rem] focus:border-0 focus:outline-hidden" onInteractOutside={() => {
            isClosingDueToPointerRef.current = true;
          }} onOpenAutoFocus={e => {
            e.preventDefault();
          }} ref={containerRef} side={side} sideOffset={sideOffset}>
                  <div>
                    <motion.div animate="animate" className={twMerge('w-72 max-w-[calc(100vw-2rem)] overflow-hidden rounded-[0.625rem] border border-gray-150 border-solid bg-white p-5 shadow-card will-change-transform dark:border-gray-800 dark:bg-gray-875 dark:text-white', className)} exit="exit" initial="initial" key="hover-card-content" style={{
                transformOrigin: 'var(--radix-popover-content-transform-origin)'
              }} transition={{
                type: 'spring',
                duration: 0.4,
                bounce: 0
              }} variants={cardAnimations}>
                      {content}
                    </motion.div>
                  </div>
                </RadixPopover.Content>}
            </AnimatePresence>
          </RadixPopover.Portal>
        </RadixPopover.Root>
      </PopoverProvider>;
});
PopoverContainer.displayName = 'PopoverContainer';
const Popover = {
  Container: PopoverContainer,
  Trigger: PopoverTrigger,
  Content: PopoverContent
};
export default Popover;